import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import logo from '../../assets/logoMoovicar.png';
import env from '../../config/env';
import { useAxios } from '../../utils/hook/useAxios';

function SignupPage() {
  const axios = useAxios();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirm: '',
    pc: false,
    cgv: false,
  });
  const [showPwd, setShowPwd] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, type, value, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const validate = () => {
    if (!form.firstName.trim()) return 'Veuillez renseigner votre prénom.';
    if (!form.lastName.trim()) return 'Veuillez renseigner votre nom.';
    if (!form.email.trim()) return 'Veuillez renseigner votre email.';
    if (!/^\S+@\S+\.\S+$/.test(form.email)) return 'Adresse email invalide.';
    if (!form.password) return 'Veuillez choisir un mot de passe.';
    if (form.password.length < 8) return 'Le mot de passe doit comporter au moins 8 caractères.';
    if (form.confirm !== form.password) return 'Les mots de passe ne correspondent pas.';
    if (!form.pc) return 'Vous devez accepter la politique de confidentialité.';
    return '';
  };

  // Force du mot de passe (0-4)
  const pwdStrength = (() => {
    const p = form.password;
    if (!p) return 0;
    let s = 0;
    if (p.length >= 8) s++;
    if (/[A-Z]/.test(p)) s++;
    if (/\d/.test(p)) s++;
    if (/[^A-Za-z0-9]/.test(p)) s++;
    return s;
  })();
  const strengthLabel = ['', 'Faible', 'Moyen', 'Bon', 'Fort'][pwdStrength];
  const strengthColor = ['', 'bg-danger', 'bg-warning', 'bg-info', 'bg-success'][pwdStrength];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const msg = validate();
    if (msg) {
      setError(msg);
      return;
    }
    try {
      setLoading(true);
      const response = await axios.post('/auth/register', {
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        password: form.password,
        accept_privacy_policy: form.pc,
        accept_marketing: form.cgv,
      });
      navigate(`/signin?registered=1&emailSent=${response.data.emailSent ? '1' : '0'}`);
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          'Inscription impossible. Veuillez réessayer.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="min-vh-100 d-flex align-items-center bg-body-tertiary py-4">
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-12 col-sm-10 col-md-8 col-lg-6 col-xl-5">
            {/* Logo */}
            <div className="text-center mb-4">
              <Link to="/">
                <img src={logo} alt="Moovicar" height="48" />
              </Link>
            </div>

            {/* Card */}
            <div className="card border-0 shadow-sm rounded-4 p-4">
              <h4 className="fw-bold mb-1 text-center">Créer un compte</h4>
              <p className="text-muted text-center mb-4" style={{ fontSize: 14 }}>
                Rejoignez la communauté Moovicar !
              </p>

              {/* Google */}
              <a
                className="btn btn-outline-secondary w-100 d-flex align-items-center justify-content-center gap-2 mb-3 rounded-3"
                href={`${env.baseUrlApi}/api/auth/google`}
                style={{ fontSize: 14, fontWeight: 500 }}
              >
                <svg width="18" height="18" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
                  <path
                    fill="#EA4335"
                    d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
                  />
                  <path
                    fill="#4285F4"
                    d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
                  />
                  <path
                    fill="#34A853"
                    d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
                  />
                  <path fill="none" d="M0 0h48v48H0z" />
                </svg>
                Continuer avec Google
              </a>

              {/* Divider */}
              <div className="d-flex align-items-center gap-2 mb-3">
                <hr className="flex-grow-1 m-0" />
                <span className="text-muted" style={{ fontSize: 12 }}>
                  ou
                </span>
                <hr className="flex-grow-1 m-0" />
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} noValidate>
                {/* Prénom / Nom */}
                <div className="row g-2 mb-3">
                  <div className="col-6">
                    <label className="form-label small fw-semibold text-muted">Prénom</label>
                    <input
                      type="text"
                      className="form-control rounded-3"
                      name="firstName"
                      placeholder="Prénom"
                      value={form.firstName}
                      onChange={handleChange}
                      autoComplete="given-name"
                    />
                  </div>
                  <div className="col-6">
                    <label className="form-label small fw-semibold text-muted">Nom</label>
                    <input
                      type="text"
                      className="form-control rounded-3"
                      name="lastName"
                      placeholder="Nom"
                      value={form.lastName}
                      onChange={handleChange}
                      autoComplete="family-name"
                    />
                  </div>
                </div>

                {/* Email */}
                <div className="mb-3">
                  <label className="form-label small fw-semibold text-muted">Email</label>
                  <input
                    type="email"
                    className="form-control rounded-3"
                    name="email"
                    placeholder="votre@email.com"
                    value={form.email}
                    onChange={handleChange}
                    autoComplete="email"
                  />
                </div>

                {/* Mot de passe */}
                <div className="mb-1">
                  <label className="form-label small fw-semibold text-muted">Mot de passe</label>
                  <div className="position-relative">
                    <input
                      type={showPwd ? 'text' : 'password'}
                      className="form-control rounded-3 pe-5"
                      name="password"
                      placeholder="8 caractères minimum"
                      value={form.password}
                      onChange={handleChange}
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      className="btn position-absolute top-50 end-0 translate-middle-y border-0 bg-transparent me-1"
                      onClick={() => setShowPwd((p) => !p)}
                      tabIndex={-1}
                      style={{ zIndex: 5 }}
                    >
                      <i className={`bi ${showPwd ? 'bi-eye-slash' : 'bi-eye'} text-muted`}></i>
                    </button>
                  </div>
                </div>

                {/* Indicateur force MDP */}
                {form.password.length > 0 && (
                  <div className="mb-3">
                    <div className="d-flex gap-1 mt-2">
                      {[1, 2, 3, 4].map((n) => (
                        <div
                          key={n}
                          className={`rounded flex-fill ${n <= pwdStrength ? strengthColor : 'bg-secondary-subtle'}`}
                          style={{ height: 4 }}
                        />
                      ))}
                    </div>
                    {strengthLabel && (
                      <p
                        className={`mb-0 mt-1 small ${['', 'text-danger', 'text-warning', 'text-info', 'text-success'][pwdStrength]}`}
                      >
                        Force : {strengthLabel}
                      </p>
                    )}
                  </div>
                )}

                {/* Confirmation mot de passe */}
                <div className="mb-3">
                  <label className="form-label small fw-semibold text-muted">
                    Confirmer le mot de passe
                  </label>
                  <div className="position-relative">
                    <input
                      type={showConfirm ? 'text' : 'password'}
                      className={`form-control rounded-3 pe-5 ${
                        form.confirm && form.password
                          ? form.confirm === form.password
                            ? 'is-valid'
                            : 'is-invalid'
                          : ''
                      }`}
                      name="confirm"
                      placeholder="Répéter le mot de passe"
                      value={form.confirm}
                      onChange={handleChange}
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      className="btn position-absolute top-50 end-0 translate-middle-y border-0 bg-transparent me-1"
                      onClick={() => setShowConfirm((p) => !p)}
                      tabIndex={-1}
                      style={{ zIndex: 5 }}
                    >
                      <i className={`bi ${showConfirm ? 'bi-eye-slash' : 'bi-eye'} text-muted`}></i>
                    </button>
                  </div>
                  {form.confirm && form.confirm !== form.password && (
                    <div className="invalid-feedback d-block" style={{ fontSize: 12 }}>
                      Les mots de passe ne correspondent pas.
                    </div>
                  )}
                  {form.confirm && form.confirm === form.password && (
                    <div className="valid-feedback d-block" style={{ fontSize: 12 }}>
                      <i className="bi bi-check-circle-fill me-1"></i>Les mots de passe
                      correspondent.
                    </div>
                  )}
                </div>

                {/* Erreur */}
                {error && (
                  <div
                    className="alert alert-danger py-2 rounded-3 d-flex align-items-center gap-2"
                    style={{ fontSize: 13 }}
                  >
                    <i className="bi bi-exclamation-circle-fill flex-shrink-0"></i>
                    {error}
                  </div>
                )}

                {/* Checkboxes */}
                <div className="mb-2">
                  <div className="form-check d-flex align-items-start gap-2 mb-2">
                    <input
                      className="form-check-input mt-1 flex-shrink-0"
                      type="checkbox"
                      name="pc"
                      id="pc"
                      checked={form.pc}
                      onChange={handleChange}
                    />
                    <label className="form-check-label" htmlFor="pc" style={{ fontSize: 13 }}>
                      J'accepte la{' '}
                      <Link to="/privacy" className="text-primary">
                        politique de confidentialité
                      </Link>{' '}
                      et les{' '}
                      <Link to="/privacy" className="text-primary">
                        conditions générales
                      </Link>
                      <span className="text-danger ms-1">*</span>
                    </label>
                  </div>

                  <div className="form-check d-flex align-items-start gap-2">
                    <input
                      className="form-check-input mt-1 flex-shrink-0"
                      type="checkbox"
                      name="cgv"
                      id="cgv"
                      checked={form.cgv}
                      onChange={handleChange}
                    />
                    <label
                      className="form-check-label text-muted"
                      htmlFor="cgv"
                      style={{ fontSize: 12 }}
                    >
                      Je veux recevoir les bons plans, promos et nouveautés —
                      <span className="text-success fw-semibold"> +5 € offerts</span> sur ma 1ʳᵉ
                      location
                    </label>
                  </div>
                </div>

                <button
                  type="submit"
                  className="btn btn-primary w-100 rounded-3 mt-3 py-2 fw-semibold"
                  disabled={
                    loading ||
                    !form.firstName.trim() ||
                    !form.lastName.trim() ||
                    !/^\S+@\S+\.\S+$/.test(form.email) ||
                    form.password.length < 8 ||
                    form.confirm !== form.password ||
                    !form.pc
                  }
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" />
                      Inscription…
                    </>
                  ) : (
                    'Créer mon compte'
                  )}
                </button>
              </form>
            </div>

            {/* Lien connexion */}
            <p className="text-center text-muted mt-3" style={{ fontSize: 14 }}>
              Déjà un compte ?{' '}
              <Link to="/signin" className="fw-semibold text-primary text-decoration-none">
                Se connecter
              </Link>
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

export default SignupPage;
